import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function FormDialog({
  open,
  onOpenChange,
  title,
  fields,
  values,
  setValues,
  onSubmit,
  submitting,
  testidPrefix = "form",
}) {
  const setField = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0C0E12] border-zinc-800 rounded-sm max-w-lg max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-zinc-100">
            {title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {fields.map((f, idx) => {
            if (f.hideWhen && f.hideWhen(values)) return null;

            if (f.type === "checkbox") {
              const checked = f.deriveChecked ? f.deriveChecked(values) : !!values[f.name];
              return (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`${testidPrefix}-${f.name}`}
                    data-testid={`${testidPrefix}-${f.name}`}
                    checked={checked}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setValues((prev) => {
                        const next = { ...prev, [f.name]: on };
                        if (f.setsField) next[f.setsField] = on ? f.setsValue : "";
                        return next;
                      });
                    }}
                    className="h-4 w-4 accent-primary cursor-pointer"
                  />
                  <Label htmlFor={`${testidPrefix}-${f.name}`} className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-400 cursor-pointer">
                    {f.label}
                  </Label>
                </div>
              );
            }

            const isDisabled = f.disabledWhen ? f.disabledWhen(values) : false;

            return (
              <div key={idx} className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {f.type === "textarea" ? (
                  <Textarea
                    data-testid={`${testidPrefix}-${f.name}`}
                    value={values[f.name] || ""}
                    onChange={(e) => setField(f.name, e.target.value)}
                    required={f.required}
                    rows={3}
                    className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary resize-none"
                    placeholder={f.placeholder || ""}
                  />
                ) : f.type === "select" ? (
                  <div className="flex items-center gap-2">
                    <Select value={values[f.name] || ""} onValueChange={(v) => setField(f.name, v)} disabled={isDisabled}>
                      <SelectTrigger
                        data-testid={`${testidPrefix}-${f.name}`}
                        className="bg-transparent border-zinc-700 rounded-sm focus:ring-primary flex-1"
                      >
                        <SelectValue placeholder="Auswählen…" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={4} className="bg-[#0C0E12] border-zinc-800 rounded-sm max-h-[280px] overflow-y-auto">
                        {f.options.map((o) => (
                          <SelectItem key={o} value={o} className="rounded-sm font-mono text-sm">
                            {o}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {f.clearable && values[f.name] ? (
                      <Button
                        type="button"
                        variant="outline"
                        data-testid={`${testidPrefix}-${f.name}-clear`}
                        onClick={() => setField(f.name, "")}
                        disabled={isDisabled}
                        title="Zurücksetzen"
                        className="rounded-sm border-zinc-700 h-9 w-9 p-0 shrink-0 text-zinc-400 hover:text-red-400"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <Input
                    data-testid={`${testidPrefix}-${f.name}`}
                    type={f.type || "text"}
                    value={values[f.name] || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (f.setsField) {
                        const n = parseFloat(v);
                        setValues((prev) => ({
                          ...prev,
                          [f.name]: v,
                          [f.setsField]: v === "" || isNaN(n) ? "" : String(Math.round(n * (f.factor || 1))),
                        }));
                      } else {
                        setField(f.name, v);
                      }
                    }}
                    required={f.required}
                    disabled={isDisabled}
                    className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary"
                    placeholder={f.placeholder || ""}
                  />
                )}
              </div>
            );
          })}
          <DialogFooter className="pt-2 gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="rounded-sm text-zinc-400"
              data-testid={`${testidPrefix}-cancel`}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-sm bg-primary hover:bg-blue-600 text-white"
              data-testid={`${testidPrefix}-submit`}
            >
              {submitting ? "Speichern…" : "Speichern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
